import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, SelectControl} from 'appShared/classes/controls';
import {
    CurrencyTypesEnum,
    HttpStatusCodesEnum,
    MarketDataFeedAndBundleModel,
    MarketDataFeedBundleInputModel,
    MarketDataFeedBundleInterface,
    PlatformFrontendInterface,
    ServerResponse,
    ServerResponseData,
    SesService
} from '@spa/api';
import {Helper} from 'appShared/classes';
import {map} from 'rxjs/operators';
import {
    AddNewFeedBundleAlaricInternalPriceSelector,
    AddNewFeedBundleControls,
    AddNewFeedBundleCostToAlaricNonProSelector,
    AddNewFeedBundleCostToAlaricProSelector,
    AddNewFeedBundleCurrencySelector,
    AddNewFeedBundleDescriptionSelector,
    AddNewFeedBundleIndividualPriceNonProSelector,
    AddNewFeedBundleIndividualPriceProSelector,
    AddNewFeedBundleNameSelector,
    AddNewFeedBundlePlatformFrontendIdSelector,
    FEEDID_CHECKBOX_PREFIX,
    prepareFeedCheckbox,
} from 'appAssets/descriptors';
import {MarketDataFeedBundleFormErrorStatesEnum} from 'appEntries/panel/enums';
import {SelectOptionInterface} from 'appShared/components';
import {InArrayValidator} from '@spa/validators';
import {BaseMarketDataForm} from 'appShared/forms';
import {MarketDataFeedsAddMessages} from 'appAssets/messages';
import { CurrencyService } from 'appShared/services';
import { CurrencyTrait } from 'appEntries/panel/traits';

export class AddFeedBundleForm extends BaseMarketDataForm {
    private readonly feeds: MarketDataFeedAndBundleModel[];
    private readonly currencyService: CurrencyService;
    private readonly MIN_SELECTED_FEED_CCOUNT = 2;
    private readonly platformFrontends: PlatformFrontendInterface[];
    public readonly FORM_ELEMENT_CCOUNT = 8;
    public platformFrontendsAutoComplete: SelectOptionInterface[];

    constructor(
        sesService: SesService,
        currencyService: CurrencyService,
        feeds: MarketDataFeedAndBundleModel[],
        platformFrontends: PlatformFrontendInterface[],
    ) {
        super(sesService);
        this.feeds = feeds;
        this.platformFrontends = platformFrontends;
        this.currencyService = currencyService;
    }

    private isFeedOrPlatformFrontendSelected(platformFrontendId, feedIds: number[]): boolean {
        return (platformFrontendId || feedIds.length >= this.MIN_SELECTED_FEED_CCOUNT);
    }

    public preparePlatformFrontendData(selectedFeedIds: number[] = []): void {
        if (!this.platformFrontends) {
            return;
        }

        this.platformFrontendsAutoComplete = [{
            label: 'No Platform Frontend',
            value: null
        }];

        const selectedFeeds = this.feeds.filter(feed => selectedFeedIds.includes(feed.id));
        const selectedPlatformFrontendIds = selectedFeeds?.map(feed => feed.platformFrontendId);
        const selectedPlatformFrontends = selectedPlatformFrontendIds && selectedPlatformFrontendIds.length > 0 ?
            this.platformFrontends.filter(platformFrontend => selectedPlatformFrontendIds.includes(platformFrontend.id)) :
            this.platformFrontends;

        const platformFrontendOptions = selectedPlatformFrontends.map((item: PlatformFrontendInterface): SelectOptionInterface => {
            return {
                label: item.name,
                value: item.id
            };
        });

        this.platformFrontendsAutoComplete.push(...platformFrontendOptions);

        const platformFrontends: number[] = this.platformFrontendsAutoComplete.map(d => {
            return Number(d.value);
        });

        const control = this.controls[2] as SelectControl;
        control.validators.push(InArrayValidator(platformFrontends));
        control.options = this.platformFrontendsAutoComplete;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        const feedControls = AddNewFeedBundleControls();

        for (const feed of this.feeds) {
            feedControls.push(prepareFeedCheckbox(feed));
        }

        this.controls = feedControls.map((control: BaseControl) => {
            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.preparePlatformFrontendData();
        this.prepareCurrencyData();
    }

    private prepareCurrencyData(): void {
        const currencies = []; 
        const control = this.controls[8] as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode)); 
        control.extendValidators(InArrayValidator(currencies));  
        control.disabled = control.options.length === 1;
    }

    createRequest(): ServerResponse<boolean> {
        const alaricProCost = this.getFormPriceValueByKey(AddNewFeedBundleCostToAlaricProSelector);
        const alaricNonProCost = this.getFormPriceValueByKey(AddNewFeedBundleCostToAlaricNonProSelector);
        const individualPricePro = this.getFormPriceValueByKey(AddNewFeedBundleIndividualPriceProSelector);
        const individualPriceNonPro = this.getFormPriceValueByKey(AddNewFeedBundleIndividualPriceNonProSelector);
        const alaricInternalPrice = this.getFormPriceValueByKey(AddNewFeedBundleAlaricInternalPriceSelector);
        const currency = this.formGroup.value[AddNewFeedBundleCurrencySelector];

        const name = this.formGroup.value[AddNewFeedBundleNameSelector];
        const description = this.formGroup.value[AddNewFeedBundleDescriptionSelector];
        const platformFrontendId = this.formGroup.value[AddNewFeedBundlePlatformFrontendIdSelector];

        const feedIds: number[] = [];

        for (const property in this.formGroup.value) {
            if (property.startsWith(FEEDID_CHECKBOX_PREFIX) && this.formGroup.value[property] > 0) {
                feedIds.push(this.formGroup.value[property]);
            }
        }

        if (!this.isFeedOrPlatformFrontendSelected(platformFrontendId, feedIds)) {
            return Helper.errorsHelper().createBadRequestMessageResponseObservable(
                MarketDataFeedsAddMessages.CreateMarketDataFeedBundleFailedRequiredFeedOrPFE.message
            );
        }

        const model = new MarketDataFeedBundleInputModel(
            name,
            description,
            currency,
            individualPricePro,
            individualPriceNonPro,
            alaricInternalPrice,
            alaricNonProCost,
            alaricProCost,
            feedIds,
            platformFrontendId
        );

        return this.sesService.createMarketDataFeedBundle(model).pipe(
            map((registerResponse: ServerResponseData<MarketDataFeedBundleInterface>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<MarketDataFeedBundleInterface>(
                        registerResponse,
                        MarketDataFeedBundleFormErrorStatesEnum.CreateMarketDataFeedBundleFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }
}
